/**
 * Live-data hooks for role dashboards. Wraps the authenticated `api()` helper
 * from session.ts and exposes a tiny set of hooks each dashboard can call
 * without knowing the underlying endpoint shapes.
 *
 * Real-or-empty rule: every hook returns { data, loading, error }. On error or
 * empty response, dashboards must render an empty state — never fall back to
 * mock numbers.
 */
import { useEffect, useRef, useState } from "react";
import { api, getSession } from "@/lib/session";

export interface LiveResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function useApi<T>(path: string | null): LiveResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(!!path);
  const [error, setError] = useState<string | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!path) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    api<T>(path)
      .then((res) => { if (!cancelled.current) { setData(res); setLoading(false); } })
      .catch((e) => {
        if (cancelled.current) return;
        setError(e?.message ?? "Could not load");
        setLoading(false);
      });
    return () => { cancelled.current = true; };
  }, [path]);

  return { data, loading, error };
}

// ─── Account shape from /api/accounts ────────────────────────────────────────
export interface ApiAccount {
  id: number;
  name: string;
  industry?: string | null;
  stage?: string | null;   // server returns 'stage' (mapped from DB status column)
  status?: string | null; // legacy alias — prefer stage
  ownerId?: number | string | null;
  estArr?: number | null;
  bucket?: string | null;
  score?: number | null;
  updatedAt?: number | string | null;
  greenCount?: number | null;
  alerts?: string[] | null;
}

export function useAccounts(): LiveResult<ApiAccount[]> {
  const session = getSession();
  const result = useApi<ApiAccount[]>(session ? "/api/accounts" : null);
  // Defensive: API may return null or a non-array on first load; normalise to array.
  const safeData = Array.isArray(result.data) ? result.data : (result.data == null ? null : []);
  return { ...result, data: safeData };
}

// ─── Plan-vs-actual (AE quota / coverage / weighted pipeline) ────────────────
export interface PlanVsActual {
  year: number;
  month: number;
  annual_quota: number;
  plan_ytd: number;
  closed_ytd: number;
  weighted_pipeline: number;
  gap: number;
  color: "green" | "yellow" | "amber" | "red" | "none";
  attainment_pct: number | null;
  has_plan: boolean;
}
export function usePlanVsActual(): LiveResult<PlanVsActual> {
  const session = getSession();
  return useApi<PlanVsActual>(session ? "/api/plan-vs-actual/me" : null);
}

// ─── Leaderboard (manager view + cross-AE rollups) ───────────────────────────
export interface LeaderboardRow {
  ae_user_id: string;
  ae_name?: string;
  ae_email?: string;
  tenant_id: number;
  open_count?: number;
  won_value?: number;
  weighted_pipeline?: number;
  commit_value?: number;
  probable_value?: number;
  upside_value?: number;
}
export function useLeaderboard(): LiveResult<LeaderboardRow[]> {
  const session = getSession();
  const result = useApi<LeaderboardRow[]>(session ? "/api/leaderboard" : null);
  const safeData = Array.isArray(result.data) ? result.data : (result.data == null ? null : []);
  return { ...result, data: safeData };
}

// ─── Hunt items per account (used to build per-AE / per-BDR cadence rollups) ─
export interface HuntItem {
  id: number;
  accountId: number;
  itemKey?: string;
  label?: string;
  status?: "open" | "chasing" | "found" | "blocked";
  owner?: string;
  evidence?: string;
  linkedCp?: number | null;
  dueDate?: string | null;
  updatedAt?: number | string;
}
export function useHuntForAccounts(accountIds: number[]): LiveResult<HuntItem[]> {
  const [data, setData] = useState<HuntItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = accountIds.join(",");
  useEffect(() => {
    let cancelled = false;
    if (!accountIds.length) { setData([]); setLoading(false); return; }
    setLoading(true);
    Promise.all(
      accountIds.map((id) =>
        api<HuntItem[]>(`/api/accounts/${id}/hunt`)
          .then((items) => items.map((h) => ({ ...h, accountId: id })))
          .catch(() => [] as HuntItem[])
      )
    )
      .then((batches) => {
        if (cancelled) return;
        setData(batches.flat());
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? "load failed"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, error };
}

// ─── Per-account checkpoint status (used for "just-cleared gates") ───────────
export interface CheckpointStatus {
  cpNumber: number;
  status: string | null;
  note?: string;
  updatedAt?: number | string;
  isManual?: boolean;
}
export function useCheckpointsForAccounts(accountIds: number[]): LiveResult<Record<number, CheckpointStatus[]>> {
  const [data, setData] = useState<Record<number, CheckpointStatus[]> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = accountIds.join(",");
  useEffect(() => {
    let cancelled = false;
    if (!accountIds.length) { setData({}); setLoading(false); return; }
    setLoading(true);
    Promise.all(
      accountIds.map((id) =>
        api<CheckpointStatus[]>(`/api/accounts/${id}/checkpoints`)
          .then((cps) => [id, cps] as [number, CheckpointStatus[]])
          .catch(() => [id, [] as CheckpointStatus[]] as [number, CheckpointStatus[]])
      )
    )
      .then((entries) => {
        if (cancelled) return;
        const out: Record<number, CheckpointStatus[]> = {};
        for (const [id, cps] of entries) out[id] = cps;
        setData(out);
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? "load failed"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, error };
}

// ─── Account scores (composite win / timing / forecast) ──────────────────────
export interface ScoreBundle {
  win: number;
  timing: number;
  forecast: number;
  cpPct: Record<number, number>;
}
export function useScoresForAccounts(accountIds: number[]): LiveResult<Record<number, ScoreBundle>> {
  const [data, setData] = useState<Record<number, ScoreBundle> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = accountIds.join(",");
  useEffect(() => {
    let cancelled = false;
    if (!accountIds.length) { setData({}); setLoading(false); return; }
    setLoading(true);
    Promise.all(
      accountIds.map((id) =>
        api<ScoreBundle>(`/api/accounts/${id}/scores`)
          .then((s) => [id, s] as [number, ScoreBundle])
          .catch(() => [id, { win: 0, timing: 0, forecast: 0, cpPct: {} }] as [number, ScoreBundle])
      )
    )
      .then((entries) => {
        if (cancelled) return;
        const out: Record<number, ScoreBundle> = {};
        for (const [id, s] of entries) out[id] = s;
        setData(out);
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? "load failed"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, error };
}

// ─── Forecast bucket math (locked) ───────────────────────────────────────────
// Floor → label per Bryan's locked rules. Server returns bucket on accounts;
// this is the fallback when score is the only signal.
export function bucketFromScore(score: number): "WON" | "COMMIT" | "PROBABLE" | "UPSIDE" | "OMITTED" {
  if (score >= 90) return "WON";
  if (score >= 80) return "COMMIT";
  if (score >= 70) return "PROBABLE";
  if (score >= 60) return "UPSIDE";
  return "OMITTED";
}

// ─── Empty-state helper ─────────────────────────────────────────────────────
export function EmptyState({
  title,
  body,
}: { title: string; body: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center">
      <div className="text-sm font-bold text-neutral-700 mb-1">{title}</div>
      <div className="text-xs text-neutral-500 leading-relaxed max-w-md mx-auto">{body}</div>
    </div>
  );
}

// ─── Loading skeleton (slate strip) ─────────────────────────────────────────
export function LoadingStrip({ height = 64 }: { height?: number }): JSX.Element {
  return (
    <div
      className="rounded-lg bg-neutral-100 animate-pulse"
      style={{ height }}
    />
  );
}

// ─── W14: AE Daily Surface types + hook ──────────────────────────────────────
export interface CpHeatmapAccount {
  id: number;
  name: string;
  account_cp_status: Array<{ cp_number: number; status: string }>;
}

export interface AeRecentAnswer {
  id: string;
  account_id: number;
  question_key: string;
  selected_option: string | null;
  evidence: string | null;
  updated_at: string;
}

export interface AeSalesPlanRow {
  id: string;
  account_id: number;
  composite_score: number | null;
  annual_quota: number | null;
  closed_ytd: number | null;
  weighted_pipeline: number | null;
  updated_at: string;
}

export interface AeCadenceAction {
  id: string;
  prospect_id: number | null;
  action_kind: string;
  outcome: string | null;
  notes: string | null;
  executed_at: string;
}

export interface AeDailySurface {
  todays_actions: AeCadenceAction[];
  cp_heatmap: CpHeatmapAccount[];
  recent_answers: AeRecentAnswer[];
  sales_plan: AeSalesPlanRow[];
  meta: { fetched_at: string; user_id: string; tenant_id: number };
}

export function useAeDailySurface(): LiveResult<AeDailySurface> {
  return useApi<AeDailySurface>("/api/ae-daily-surface");
}

// ─── W13: BDR Daily Surface types + hook ─────────────────────────────────────
// cadence_steps = active cadence_enrollments for this BDR due today or overdue
export interface BdrCadenceStep {
  id: string;
  account_name: string | null;
  contact_name: string | null;
  current_step: number;
  status: string;
  next_action_at: string | null;
  last_action_at: string | null;
  marketing_hook_kind: string | null;
  source: string | null;
  enrolled_at: string;
}

export interface BdrCadenceTouch {
  id: string;
  prospect_id: number | null;
  action_kind: string;
  outcome: string | null;
  notes: string | null;
  executed_at: string;
}

export interface BdrHuntItem {
  id: string;
  account_id: number | null;
  item_key: string;
  label: string;
  status: string;
  note: string | null;
  linked_cp: number | null;
  due_date: string | null;
  updated_at: string;
}

export interface BdrProspectAccount {
  id: number;
  name: string;
  account_cp_status: Array<{ cp_number: number; status: string }>;
}

export interface BdrDailySurface {
  cadence_steps: BdrCadenceStep[];
  todays_touches: BdrCadenceTouch[];
  open_hunts: BdrHuntItem[];
  prospect_coverage: BdrProspectAccount[];
  meta: { fetched_at: string; user_id: string; tenant_id: number };
}

export function useBdrDailySurface(): LiveResult<BdrDailySurface> {
  return useApi<BdrDailySurface>("/api/bdr-daily-surface");
}

// ─── Answered question keys per account (W9-NextCpCta) ───────────────────────
//
// Fetches the set of answered question_keys for each account in the provided
// list. Used by the AE Daily "Deals to Deepen" section to compute the next
// unanswered Checkpoint question per deal card.
//
// Each entry in the returned map is a Set<string> of question_keys.
// If an account's fetch fails, its entry is an empty Set (safe fallback).

export function useAnsweredKeysForAccounts(
  accountIds: number[]
): LiveResult<Map<number, Set<string>>> {
  const [data, setData] = useState<Map<number, Set<string>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = accountIds.join(",");

  useEffect(() => {
    let cancelled = false;
    if (!accountIds.length) { setData(new Map()); setLoading(false); return; }
    setLoading(true);
    Promise.all(
      accountIds.map((id) =>
        api<Array<{ key: string; answer: { selectedOption: string | null } | null }>>(
          `/api/accounts/${id}/questions`
        )
          .then((qs) => {
            const answered = new Set<string>(
              (qs ?? []).filter((q) => q.answer?.selectedOption != null).map((q) => q.key)
            );
            return [id, answered] as [number, Set<string>];
          })
          .catch(() => [id, new Set<string>()] as [number, Set<string>])
      )
    )
      .then((entries) => {
        if (cancelled) return;
        const out = new Map<number, Set<string>>();
        for (const [id, keys] of entries) out.set(id, keys);
        setData(out);
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) { setError(e?.message ?? "load failed"); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
